import { ConfettiConfig } from './Confetti.types';
import {
  animate,
  generatePhysics,
  getRandomInt,
} from '../../functions/helpers';
import { Particle } from '../../Main.types';

const defaultColors = ['#A45BF1', '#25C6F6', '#72F753', '#F76C88', '#F5F770'];
const factors = [-0.6, -0.3, 0, 0.3, 0.6];

const createElements = (
  root: Element,
  elementCount: number,
  elementSize: number,
  zIndex: number,
  position: string,
  colors: string[]
) =>
  Array.from({ length: elementCount }).map((_, index) => {
    const element = document.createElement('span');
    element.style['background-color'] = colors[index % colors.length];
    element.style.width = `${elementSize}px`;
    element.style.height = `${elementSize}px`;
    element.style.position = position;
    element.style.zIndex = `${zIndex}`;
    root.appendChild(element);
    return { element, differentiator: getRandomInt(0, factors.length) };
  });

const updateParticle = (
  particle: Particle,
  progress: number,
  decay: number,
  lifetime: number // Needed if we want to scale increments based on total time
) => {
  const {
    x: x0,
    y: y0,
    z: z0,
    tiltAngle: tiltAngle0,
    wobble: wobble0,
    angle2D,
    angle3D,
    velocity: velocity0,
    differentiator,
  } = particle.physics;

  // For decay integration:
  // velocity(p) = velocity0 * decay^p
  // The integrated factor for velocity under exponential decay over progress:
  const velocityDecayTerm =
    decay !== 1 && decay > 0
      ? (decay ** progress - 1) / Math.log(decay)
      : progress; // If decay=1, no decay, integral simplifies to velocity0 * progress

  // Incremental positions
  const incrementX = Math.cos(angle2D) * velocity0 * velocityDecayTerm;
  const incrementY =
    Math.sin(angle2D) * velocity0 * velocityDecayTerm + 3.5 * progress;
  const incrementZ = Math.sin(angle3D) * velocity0 * velocityDecayTerm;

  const currentX = x0 + incrementX;
  const currentY = y0 + incrementY;
  const currentZ = z0 + incrementZ;

  // Assume ~60 frames/sec and scale increments accordingly
  const assumedFrames = 60 * (lifetime / 1000);
  const wobbleIncrementRate = 0.05 * assumedFrames; // originally wobble += 0.05 each frame
  const tiltIncrementRate = 0.15 * assumedFrames; // originally tiltAngle += 0.15 each frame

  const currentWobble = wobble0 + wobbleIncrementRate * progress;
  const currentTiltAngle = tiltAngle0 + tiltIncrementRate * progress;

  const wobbleX =
    currentX +
    (factors[differentiator] * progress * currentWobble * currentWobble +
      20 *
        (differentiator % 2
          ? Math.sin(currentWobble / 4)
          : Math.cos(currentWobble / 4)));

  const wobbleY = currentY + 5 * Math.sin(currentWobble);

  // Scale and opacity adjustments
  const currentScale = 1 - 0.2 * progress;
  let currentOpacity = 1;
  if (progress > 0.5) {
    currentOpacity = 2 - 2 * progress;
  }

  // Update element styles
  particle.element.style.transform = `translate3d(${wobbleX}px, ${wobbleY}px, ${currentZ}px) rotate3d(1, 1, 1, ${
    differentiator % 2 ? currentTiltAngle : -1 * currentTiltAngle
  }rad)`;

  particle.element.style.scale = `${currentScale}`;
  particle.element.style.opacity = `${currentOpacity}`;
};

export const confetti = (
  root: Element,
  internalAnimatingCallback: () => void,
  config?: ConfettiConfig
) => {
  const options = config || {};
  const {
    elementCount = 50,
    elementSize = 8,
    colors = defaultColors,
    angle = 90,
    spread = 45,
    decay = 0.94,
    lifetime = 200,
    startVelocity = 35,
    zIndex = 0,
    position = 'fixed',
    onAnimationComplete,
  } = options;
  const spanElements = createElements(
    root,
    elementCount,
    elementSize,
    zIndex,
    position,
    colors
  );
  const particles = spanElements.map(({ element, differentiator }) => ({
    element,
    physics: generatePhysics(angle, spread, startVelocity, differentiator),
  }));

  const onFinish = () => {
    if (typeof onAnimationComplete === 'function') {
      onAnimationComplete();
    }
    internalAnimatingCallback();
  };

  animate({
    root,
    particles,
    decay,
    lifetime,
    updateParticle,
    onFinish,
  });
};
